import { Badge, Box, Hidden, styled } from "@mui/material";
import { useEffect, useState } from "react";
import * as React from "react";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { useColorMode } from "@/providers/theme/color";
import {
  NotificationRecord,
  NotificationChannel,
  ReadStatus,
} from "@/shared/domain/notifications";
import { useOrgMemberDirectory } from "@/shared/domain/orgs/hooks";
import {
  ContrastIcon,
  DarkModeOutlinedIcon,
  LightModeOutlinedIcon,
  NotificationsOutlinedIcon,
} from "@/shared/foundation/icons/mui";
import { usePaginatedAPICall } from "@/shared/services/apiHooks";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { RobotoAPICall } from "@/types";

import { CheckOutOrgButton } from "../../CheckOutOrgButton";
import { RobotoIconButton } from "../../RobotoIconButton";
import { RobotoTooltip } from "../../RobotoTooltip";
import { FeedbackIcon } from "../FeedbackIcon";
import { NotificationsPopover } from "../NotificationsPopover/NotificationsPopover";
import { SupportPopover } from "../SupportPopover";
import { notificationsEndpoint } from "../types";

const NavItems = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",

  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    padding: theme.spacing(1),
  },
}));

interface RobotoTopBarActionsProps {}

const pageSize = 100;

export const RobotoTopBarActions: React.FC<RobotoTopBarActionsProps> = () => {
  const { colorMode, toggleColorMode } = useColorMode();
  const { currentOrganization } = useAuth();
  const memberDirectory = useOrgMemberDirectory(currentOrganization?.org_id);
  const { notifications } = useDomainServices();

  const [pageNumber, setPageNumber] = useState<number>(0);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const [loadingMarkAllAsRead, setLoadingMarkAllAsRead] =
    useState<boolean>(false);
  const [supportAnchorEl, setSupportAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  const {
    getFirstPage,
    fetchNextPage,
    loading,
    cache,
    isNextPageAvailable,
    updateItemsInCache,
  } = usePaginatedAPICall<NotificationRecord>();

  let badgeCount = 0;
  let allNotificationsRead = true;

  cache.forEach((notification) => {
    const isAcknowledged =
      notification.lifecycle_status[NotificationChannel.WebUi] ===
      "Acknowledged";

    if (!isAcknowledged) {
      // Only increment count for notifications that have not been acknowledged
      badgeCount++;
    }

    if (notification.read_status === ReadStatus.Unread) {
      allNotificationsRead = false;
    }
  });

  let badgeContent = badgeCount.toString();
  if (badgeCount >= 9) {
    badgeContent = "9+";
  }

  useEffect(() => {
    const fetchData = async () => {
      const apiCall: RobotoAPICall = {
        endpoint: notificationsEndpoint,
        method: "GET",
        orgId: currentOrganization?.org_id ?? "",
      };

      await getFirstPage(apiCall, pageSize);

      setLastFetchTime(Date.now());
    };

    void fetchData();
  }, [currentOrganization?.org_id, getFirstPage]);

  const handleSupportClose = () => {
    setSupportAnchorEl(null);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleMarkAllNotificationsAsRead = () => {
    setLoadingMarkAllAsRead(true);
    notifications
      .markNotificationsAsRead(cache, currentOrganization?.org_id || "")
      .then((updatedNotifications) => {
        const dict: { [key: string]: NotificationRecord } = {};

        updatedNotifications.forEach((notification) => {
          dict[notification.notification_id] = notification;
        });

        updateItemsInCache(dict, "notification_id");
      })
      .catch((error) => {
        ErrorMonitoringService.captureError(error);
      })
      .finally(() => {
        setLoadingMarkAllAsRead(false);
      });
  };

  const handleNotificationUpdate = (notification: NotificationRecord) => {
    const dict: { [key: string]: NotificationRecord } = {};

    dict[notification.notification_id] = notification;

    updateItemsInCache(dict, "notification_id");

    handleNotificationsClose();
  };

  const handleLoadMore = () => {
    void fetchNextPage(pageNumber + 1, pageSize, "url");
    setPageNumber(pageNumber + 1);
  };

  const [avatarMap, setAvatarMap] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const avatarMap: { [key: string]: string } = {};

    memberDirectory.forEach((member, memberId) => {
      if (member.picture_url) {
        avatarMap[memberId] = member.picture_url;
      }
    });

    setAvatarMap(avatarMap);
  }, [memberDirectory]);

  const colorModeToIcon: Record<string, JSX.Element> = {
    auto: <ContrastIcon sx={{ width: 20, height: 20 }} />,
    dark: <DarkModeOutlinedIcon sx={{ width: 20, height: 20 }} />,
    light: <LightModeOutlinedIcon sx={{ width: 20, height: 20 }} />,
  };

  const colorModeToTooltipTitle: Record<string, string> = {
    auto: "Theme: Auto",
    dark: "Theme: Dark",
    light: "Theme: Light",
  };

  return (
    <Box>
      <Hidden smDown implementation="css">
        <NavItems>
          <CheckOutOrgButton />

          <RobotoTooltip title={"Support"}>
            <span>
              <RobotoIconButton
                eventName={"SendFeedbackFormOpened"}
                onClick={(e) => {
                  setSupportAnchorEl(e.currentTarget);
                }}
              >
                <FeedbackIcon />
              </RobotoIconButton>
            </span>
          </RobotoTooltip>

          <RobotoTooltip title={"Notifications"}>
            <span>
              <RobotoIconButton
                eventName={"ViewNotificationsClicked"}
                data-cy={"notifications-icon"}
                onClick={(e) => {
                  if (cache.length > 0) {
                    notifications
                      .markNotificationsAcknowledged(
                        cache,
                        currentOrganization?.org_id || "",
                      )
                      .then((updatedNotifications) => {
                        const dict: { [key: string]: NotificationRecord } = {};

                        updatedNotifications.forEach((notification) => {
                          dict[notification.notification_id] = notification;
                        });

                        updateItemsInCache(dict, "notification_id");
                      })
                      .catch((error) => {
                        ErrorMonitoringService.captureError(error);
                      });
                  }

                  setNotificationsAnchorEl(e.currentTarget);
                }}
              >
                {badgeCount === 0 ? (
                  <NotificationsOutlinedIcon sx={{ width: 20, height: 20 }} />
                ) : (
                  <Badge
                    data-cy={"notifications-badge"}
                    badgeContent={badgeContent}
                    color="error"
                  >
                    <NotificationsOutlinedIcon sx={{ width: 20, height: 20 }} />
                  </Badge>
                )}
              </RobotoIconButton>
            </span>
          </RobotoTooltip>

          <RobotoTooltip title={colorModeToTooltipTitle[colorMode]}>
            <span>
              <RobotoIconButton
                onClick={toggleColorMode}
                eventName={"ColorModeToggleClicked"}
                eventProperties={{ mode: colorMode }}
              >
                {colorModeToIcon[colorMode]}
              </RobotoIconButton>
            </span>
          </RobotoTooltip>
        </NavItems>
      </Hidden>
      <SupportPopover
        anchorEl={supportAnchorEl}
        handleClose={handleSupportClose}
      />
      <NotificationsPopover
        anchorEl={notificationsAnchorEl}
        handleClose={handleNotificationsClose}
        notifications={cache}
        isNextPageAvailable={isNextPageAvailable}
        handleNotificationUpdate={handleNotificationUpdate}
        handleLoadMore={handleLoadMore}
        pageLoading={loading}
        allNotificationsRead={allNotificationsRead}
        handleMarkAllAsRead={handleMarkAllNotificationsAsRead}
        loadingMarkAllAsRead={loadingMarkAllAsRead}
        lastUpdateTimestamp={lastFetchTime}
        avatarMap={avatarMap}
      />
    </Box>
  );
};
